<script setup lang="ts">
import { Button } from '~ds/components/ui/button';
import { Heading, Text } from '~ds/components/ui/typography';
import { useI18n } from 'vue-i18n';

import type { FeedbackSurveyQuestionScale } from './types';

import { areFeedbackSurveyScaleEndLabelsPaired, feedbackQuestionLabel } from './types';

const props = defineProps<{
  question: FeedbackSurveyQuestionScale;
}>();

const { t } = useI18n();

const model = defineModel<number | undefined>();

const label = computed(() => feedbackQuestionLabel(props.question, t));

const range = computed(() => {
  const items: number[] = [];
  for (let i = props.question.min; i <= props.question.max; i++) {
    items.push(i);
  }
  return items;
});

const minLabel = computed(() =>
  'minLabel' in props.question ? props.question.minLabel : undefined,
);
const maxLabel = computed(() =>
  'maxLabel' in props.question ? props.question.maxLabel : undefined,
);

const isWideRange = computed(() => props.question.max - props.question.min >= 9);

const showScaleEndLabels = computed(() => {
  if (!areFeedbackSurveyScaleEndLabelsPaired(minLabel.value, maxLabel.value)) {
    if (import.meta.env.DEV) {
      console.warn(
        '[KUIUniCompletionFeedbackSurveyScale] minLabel and maxLabel must both be set or both omitted.',
        { minLabel: minLabel.value, maxLabel: maxLabel.value },
      );
    }
    return false;
  }
  return minLabel.value != null && maxLabel.value != null;
});
</script>

<template>
  <div class="flex flex-col items-center gap-4">
    <Heading
      size="md"
      as="h3"
      class="text-center max-w-sm"
    >
      {{ label }}
    </Heading>
    <!-- Wraps rather than scrolls or clips: eleven 40px buttons plus their gaps are ~500px, wider
         than any phone, and a student who cannot reach "10" cannot answer a required question. -->
    <div
      class="flex max-w-full flex-wrap justify-center"
      :class="[isWideRange ? 'gap-1.5' : 'gap-3']"
      role="radiogroup"
      :aria-label="label"
    >
      <Button
        v-for="n in range"
        :key="n"
        type="button"
        role="radio"
        size="icon-lg"
        :variant="model === n ? 'default' : 'outline'"
        :aria-checked="model === n"
        class="font-semibold"
        :class="[
          isWideRange ? 'text-sm' : 'size-14 text-lg',
          { 'scale-110': model === n },
        ]"
        :aria-label="t('unified.common.rateOf', { value: n, max: question.max })"
        @click="model = n"
      >
        {{ n }}
      </Button>
    </div>
    <div
      v-if="showScaleEndLabels"
      class="flex w-full max-w-xs justify-between"
    >
      <Text
        size="sm"
        class="text-muted-foreground"
      >
        {{ minLabel }}
      </Text>
      <Text
        size="sm"
        class="text-muted-foreground"
      >
        {{ maxLabel }}
      </Text>
    </div>
  </div>
</template>
